import { useMemo, useRef, useEffect, useState, ReactNode } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Checkbox,
  Tooltip,
  FormControlLabel,
  Switch,
  Fade,
} from "@mui/material";
import InfoSnackbar from "../InfoSnackbar";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import TodayIcon from "@mui/icons-material/Today";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LinkIcon from "@mui/icons-material/Link";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { CalendarEvent } from "../../models/calendarEvent";
import CalendarSourceMarker from "../calendarSourceMarker";
import { CalendarConnectionStatusMap } from "../../util/calendarEvent";

// ── helpers ────────────────────────────────────────────────────────
function dateKey(d: Date | string): string {
  return dayjs(d).format("YYYY-MM-DD");
}

function groupByDate(
  events: CalendarEvent[],
): [string, CalendarEvent[]][] {
  const groups: Record<string, CalendarEvent[]> = {};
  for (const event of events) {
    if (!event.startDate) continue;
    const k = dateKey(event.startDate);
    if (!groups[k]) groups[k] = [];
    groups[k].push(event);
  }
  for (const date in groups) {
    groups[date].sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    );
  }
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

interface ConflictMeta {
  groupId: number | null;
  groupSize: number;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
}

function computeConflictGroups(events: CalendarEvent[]): ConflictMeta[] {
  const meta: ConflictMeta[] = events.map(() => ({
    groupId: null,
    groupSize: 1,
    isFirstInGroup: true,
    isLastInGroup: true,
  }));
  let currentGroup: number[] = [];
  let runningMaxEnd = 0;
  let groupCounter = 0;
  const flush = () => {
    if (currentGroup.length >= 2) {
      groupCounter += 1;
      currentGroup.forEach((idx, i) => {
        meta[idx] = {
          groupId: groupCounter,
          groupSize: currentGroup.length,
          isFirstInGroup: i === 0,
          isLastInGroup: i === currentGroup.length - 1,
        };
      });
    }
    currentGroup = [];
    runningMaxEnd = 0;
  };
  events.forEach((ev, idx) => {
    const start = new Date(ev.startDate).getTime();
    const end = ev.endDate ? new Date(ev.endDate).getTime() : start;
    if (currentGroup.length === 0) {
      currentGroup = [idx];
      runningMaxEnd = end;
      return;
    }
    if (start < runningMaxEnd) {
      currentGroup.push(idx);
      if (end > runningMaxEnd) runningMaxEnd = end;
    } else {
      flush();
      currentGroup = [idx];
      runningMaxEnd = end;
    }
  });
  flush();
  return meta;
}

function formatTime(d?: Date | string): string {
  if (!d) return "";
  return dayjs(d).format("HH:mm");
}

// ── selection / linkage props ─────────────────────────────────────
export interface MonthlyCalendarSelectionProps {
  selectedIds: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
  onToggleAllVisible: (checked: boolean) => void;
  linkedClientByEventId: Record<string, string>;
  onOpenLinkDialog: () => void;
  isLinking: boolean;
  onDisconnectClient?: (eventId: string) => void;
}

export interface MonthlyCalendarViewProps {
  events: CalendarEvent[];
  loading: boolean;

  viewedMonth: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSetMonth: (d: Date) => void;
  availableMonths: Date[];

  searchText: string;
  onSearchChange: (v: string) => void;
  isSearchActive: boolean;
  searchInProgress?: boolean;

  connectionsSlot: ReactNode;
  onRefresh: () => void;

  onEventClick: (event: CalendarEvent) => void;
  /** Called when the row-chevron is clicked. Falls back to onEventClick if absent. */
  onChevronClick?: (event: CalendarEvent) => void;
  /** Called on row double-click or Enter key. Falls back to onEventClick if absent. */
  onRowDoubleClick?: (event: CalendarEvent) => void;

  selection?: MonthlyCalendarSelectionProps;
  enableConflictDetection?: boolean;
  connectionStatuses?: CalendarConnectionStatusMap;

  /** Page title shown on the left of the top toolbar (e.g. "Your Calendar"). */
  title?: string;
  /** When provided, shows a "Create meeting" button in the top toolbar. */
  onCreateMeeting?: () => void;
  /** Controlled toggle: include past events in search results. */
  includePastInSearch?: boolean;
  onTogglePastInSearch?: (v: boolean) => void;
}

// ── EventRow ──────────────────────────────────────────────────────
interface EventRowProps {
  event: CalendarEvent;
  conflict: ConflictMeta;
  isPastDay: boolean;
  isActive: boolean;
  selection?: MonthlyCalendarSelectionProps;
  onEventClick: (e: CalendarEvent) => void;
  onChevronClick?: (e: CalendarEvent) => void;
  onRowDoubleClick?: (e: CalendarEvent) => void;
  rowRef?: React.Ref<HTMLLIElement>;
}

const EventRow = ({
  event,
  conflict,
  isPastDay,
  isActive,
  selection,
  onEventClick,
  onChevronClick,
  onRowDoubleClick,
  rowRef,
}: EventRowProps) => {
  const { t } = useTranslation();
  const eventId = event.id || event.uid || "";
  const isSelected = selection ? selection.selectedIds.has(eventId) : false;
  const inConflict = conflict.groupId !== null;
  const clientName = selection?.linkedClientByEventId[eventId];

  const innerLeftBorder = inConflict
    ? "3px solid #F23D47"
    : isActive
      ? "3px solid #B89D5E"
      : isSelected
        ? "3px solid #242C63"
        : "3px solid transparent";

  const startT = formatTime(event.startDate);
  const endT = formatTime(event.endDate);

  // ── click debounce (200 ms) so double-click doesn't also fire single-click ──
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const scheduleToggle = () => {
    // Always reset the timer so the 200 ms window is anchored to the LAST
    // click — not the first. This ensures a slow double-click (gap > 200 ms)
    // still lets handleDoubleClick cancel the pending toggle before it fires.
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      if (selection) {
        selection.onToggle(eventId, !isSelected);
      } else {
        onEventClick(event);
      }
    }, 200);
  };

  const handleDoubleClick = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    (onRowDoubleClick ?? onEventClick)(event);
  };

  const handleOpenDetails = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    (onRowDoubleClick ?? onEventClick)(event);
  };

  const bgColor = isSelected
    ? "#F6F4ED"
    : isActive
      ? "#FBF7EE"
      : "transparent";

  return (
    <Box
      component="li"
      ref={rowRef}
      sx={{
        borderBottom: "1px solid #E5E7EB",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Box
        onClick={scheduleToggle}
        onDoubleClick={handleDoubleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleOpenDetails();
          }
        }}
        sx={{
          display: "grid",
          gridTemplateColumns: selection
            ? "44px 72px 140px 1.4fr 1fr 200px 140px 40px"
            : "72px 140px 1.4fr 1fr 200px 140px 40px",
          alignItems: "stretch",
          minHeight: 56,
          cursor: "pointer",
          userSelect: "none",
          backgroundColor: bgColor,
          fontFamily: '"Inter Tight", sans-serif',
          transition: "background-color 0.15s",
          "&:hover": {
            backgroundColor: isSelected ? "#F1ECDE" : "#F8FAFC",
          },
        }}
      >
        {selection && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => {
              // stopPropagation prevents the outer grid's onClick from also
              // calling scheduleToggle, but we do NOT stop dblclick here —
              // it bubbles to the outer grid's onDoubleClick (handleDoubleClick)
              // which clears the pending timer and opens Meeting Details.
              e.stopPropagation();
              scheduleToggle();
            }}
          >
            <Checkbox
              size="small"
              checked={isSelected}
              tabIndex={-1}
              sx={{
                p: 0.5,
                color: "#828CA1",
                "&.Mui-checked": { color: "#242C63" },
                pointerEvents: "none",
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 1.75,
            pl: 1.5,
            borderLeft: innerLeftBorder,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <CalendarSourceMarker
            provider={event.provider}
            accountUsername={event.accountUsername}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 1.75,
            borderRight: "1px solid #E5E7EB",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontVariantNumeric: "tabular-nums",
              color: isActive ? "#1A2048" : isPastDay ? "#828CA1" : "#64748B",
              fontFamily: '"Inter Tight", sans-serif',
            }}
          >
            {startT} – {endT}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            py: 1.75,
            pl: 1.5,
          }}
        >
          {isActive && (
            <FiberManualRecordIcon
              sx={{ fontSize: 10, color: "#B89D5E", flexShrink: 0 }}
            />
          )}
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: isPastDay && !isActive ? "#64748B" : "#1A2048",
              fontFamily: '"Inter Tight", sans-serif',
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.name}
          </Typography>
          {isActive && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                backgroundColor: "#F4EBD6",
                color: "#7A6534",
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {t("calendar.nowNext", { defaultValue: "Now / Next" })}
            </Box>
          )}
          {inConflict && !conflict.isFirstInGroup && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                backgroundColor: "#FDE7E9",
                color: "#A8141C",
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {t("calendar.overlaps", { defaultValue: "Overlaps" })}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            py: 1.75,
            pl: 1.5,
          }}
        >
          <LocationOnIcon sx={{ fontSize: 15, color: "#828CA1", flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: "#64748B",
              fontFamily: '"Inter Tight", sans-serif',
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.location ? (
              event.location
            ) : (
              <Box
                component="span"
                sx={{ color: "#C4C9D4", fontStyle: "italic", fontSize: "0.8rem" }}
              >
                {t("calendar.noLocation", { defaultValue: "No location" })}
              </Box>
            )}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            overflow: "hidden",
            py: 1.75,
            pl: 1.5,
          }}
        >
          <PersonIcon sx={{ fontSize: 15, color: "#828CA1", flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: "#64748B",
              fontFamily: '"Inter Tight", sans-serif',
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.creatorName || event.creator || ""}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            py: 1.75,
            pr: 1.5,
          }}
        >
          {selection && clientName && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                maxWidth: "100%",
                fontSize: "12px",
                fontWeight: 600,
                pl: 1.25,
                pr: 0.5,
                py: 0.5,
                borderRadius: "999px",
                backgroundColor: "#1A2048",
                color: "#FFFFFF",
                fontFamily: '"Inter Tight", sans-serif',
              }}
              title={clientName}
            >
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {clientName}
              </Box>
              {selection.onDisconnectClient && (
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    selection.onDisconnectClient?.(eventId);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      e.preventDefault();
                      selection.onDisconnectClient?.(eventId);
                    }
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    cursor: "pointer",
                    color: "#FFFFFF",
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Chevron — opens Meeting Details; isolated from selection */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={(e) => {
            e.stopPropagation();
            (onChevronClick ?? onEventClick)(event);
          }}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <Tooltip
            title={t("calendar.openMeetingDetails", {
              defaultValue: "Open meeting details",
            })}
          >
            <IconButton
              size="small"
              tabIndex={-1}
              sx={{ color: "#C4C9D4", "&:hover": { color: "#242C63" } }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// ── DayGroup ──────────────────────────────────────────────────────
interface DayGroupProps {
  date: string;
  events: CalendarEvent[];
  isTodayGroup: boolean;
  isPastDay: boolean;
  activeEventId: string | null;
  selection?: MonthlyCalendarSelectionProps;
  onEventClick: (e: CalendarEvent) => void;
  onChevronClick?: (e: CalendarEvent) => void;
  onRowDoubleClick?: (e: CalendarEvent) => void;
  enableConflictDetection: boolean;
  dayRef?: React.Ref<HTMLDivElement>;
}

const DayGroup = ({
  date,
  events,
  isTodayGroup,
  isPastDay,
  activeEventId,
  selection,
  onEventClick,
  onChevronClick,
  onRowDoubleClick,
  enableConflictDetection,
  dayRef,
}: DayGroupProps) => {
  const { t } = useTranslation();
  const conflicts = useMemo(
    () =>
      enableConflictDetection
        ? computeConflictGroups(events)
        : events.map(() => ({
            groupId: null,
            groupSize: 1,
            isFirstInGroup: true,
            isLastInGroup: true,
          })),
    [events, enableConflictDetection],
  );
  const conflictGroupIds = new Set<number>();
  conflicts.forEach((c) => {
    if (c.groupId !== null) conflictGroupIds.add(c.groupId);
  });
  const conflictCount = conflictGroupIds.size;

  const parsed = dayjs(date);
  const weekday = parsed.format("ddd").toUpperCase();
  const dayNum = parsed.format("D");
  const month = parsed.format("MMM").toUpperCase();

  const dateLabel = (() => {
    if (parsed.isSame(dayjs(), "day"))
      return t("calendar.today", { defaultValue: "Today" });
    if (parsed.isSame(dayjs().add(1, "day"), "day"))
      return t("calendar.tomorrow", { defaultValue: "Tomorrow" });
    return parsed.format("dddd, MMMM D, YYYY");
  })();

  return (
    <Box
      ref={dayRef}
      data-date={date}
      sx={{
        mb: 2.5,
        backgroundColor: "#FFFFFF",
        border: isTodayGroup ? "2px solid #242C63" : "1px solid #E5E7EB",
        borderRadius: "8px",
        overflow: "hidden",
        scrollMarginTop: 130,
      }}
    >
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "stretch",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            minWidth: 140,
            flexShrink: 0,
            backgroundColor: isTodayGroup ? "#242C63" : "#F8FAFC",
            borderRight: isTodayGroup ? "none" : "3px solid #242C63",
            color: isTodayGroup ? "#FFFFFF" : isPastDay ? "#828CA1" : "#1A2048",
            fontFamily: '"Inter Tight", sans-serif',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              letterSpacing: "0.04em",
              whiteSpace: "nowrap",
              lineHeight: 1,
              py: 1.25,
              fontFamily: '"Inter Tight", sans-serif',
            }}
          >
            {weekday} · {dayNum} {month}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flex: 1,
            px: 2,
            py: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              component="h2"
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                lineHeight: 1,
                color: isPastDay ? "#64748B" : "#1A2048",
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {dateLabel}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "0.75rem",
                lineHeight: 1,
                color: "#828CA1",
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {events.length === 1
                ? t("calendar.eventCount_one", {
                    count: events.length,
                    defaultValue: "1 event",
                  })
                : t("calendar.eventCount_other", {
                    count: events.length,
                    defaultValue: `${events.length} events`,
                  })}
              {conflictCount > 0 && (
                <>
                  {" · "}
                  {t("calendar.conflict", {
                    count: conflictCount,
                    defaultValue:
                      conflictCount === 1
                        ? `${conflictCount} conflict`
                        : `${conflictCount} conflicts`,
                  })}
                </>
              )}
            </Typography>
          </Box>
          {conflictCount > 0 && (
            <Box
              component="span"
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                px: 1,
                py: 0.5,
                borderRadius: "999px",
                backgroundColor: "#FDE7E9",
                color: "#A8141C",
                border: "1px solid #F23D47",
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {t("calendar.conflict", {
                count: conflictCount,
                defaultValue:
                  conflictCount === 1
                    ? `${conflictCount} conflict`
                    : `${conflictCount} conflicts`,
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
        {events.map((ev, idx) => (
          <EventRow
            key={ev.id || ev.uid || `${ev.name}-${idx}`}
            event={ev}
            conflict={conflicts[idx]}
            isPastDay={isPastDay}
            isActive={(ev.id || ev.uid) === activeEventId}
            selection={selection}
            onEventClick={onEventClick}
            onChevronClick={onChevronClick}
            onRowDoubleClick={onRowDoubleClick}
          />
        ))}
      </Box>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────
const MonthlyCalendarView = ({
  events,
  loading,
  viewedMonth,
  onPrevMonth,
  onNextMonth,
  onSetMonth,
  availableMonths,
  searchText,
  onSearchChange,
  isSearchActive,
  searchInProgress,
  connectionsSlot,
  onRefresh,
  onEventClick,
  onChevronClick,
  onRowDoubleClick,
  selection,
  enableConflictDetection = true,
  title,
  onCreateMeeting,
  includePastInSearch,
  onTogglePastInSearch,
}: MonthlyCalendarViewProps) => {
  const { t } = useTranslation();
  const [monthMenuAnchor, setMonthMenuAnchor] =
    useState<HTMLElement | null>(null);
  const todayRef = useRef<HTMLDivElement>(null);
  const [noTodayEventsSnackbar, setNoTodayEventsSnackbar] = useState(false);

  const grouped = useMemo(() => groupByDate(events), [events]);
  const todayKey = dayjs().format("YYYY-MM-DD");
  const currentRealMonth = dayjs().startOf("month");

  // Determine active event for "Now / Next" highlight.
  const activeEventId = useMemo<string | null>(() => {
    const now = Date.now();
    // currently running
    for (const ev of events) {
      const s = new Date(ev.startDate).getTime();
      const e = ev.endDate ? new Date(ev.endDate).getTime() : s;
      if (s <= now && now < e) return ev.id || ev.uid || null;
    }
    // earliest upcoming today
    const today = dayjs().format("YYYY-MM-DD");
    const upcomingToday = events
      .filter(
        (ev) =>
          dayjs(ev.startDate).format("YYYY-MM-DD") === today &&
          new Date(ev.startDate).getTime() > now,
      )
      .sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      );
    return upcomingToday.length > 0
      ? upcomingToday[0].id || upcomingToday[0].uid || null
      : null;
  }, [events]);

  const handleScrollToToday = () => {
    if (!dayjs(viewedMonth).isSame(currentRealMonth, "month")) {
      // Switch to current month first, then scroll — don't check events yet
      onSetMonth(currentRealMonth.toDate());
      setTimeout(() => {
        todayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
      return;
    }
    const hasTodayEvents = events.some(
      (ev) => dayjs(ev.startDate).format("YYYY-MM-DD") === todayKey,
    );
    if (!hasTodayEvents) {
      setNoTodayEventsSnackbar(true);
      return;
    }
    todayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Select-all toolbar handlers
  const visibleIds = grouped.flatMap(([, evs]) =>
    evs.map((e) => e.id || e.uid || ""),
  );
  const totalVisible = visibleIds.length;
  const selectedVisibleCount = selection
    ? visibleIds.filter((id) => selection.selectedIds.has(id)).length
    : 0;
  const allSelected = selection
    ? totalVisible > 0 && selectedVisibleCount === totalVisible
    : false;
  const someSelected = selection
    ? selectedVisibleCount > 0 && selectedVisibleCount < totalVisible
    : false;

  useEffect(() => {
    // no-op effect placeholder for future side-effects
  }, [viewedMonth]);

  return (
    <Box sx={{ width: "100%", fontFamily: '"Inter Tight", sans-serif' }}>
      {/* Toolbar */}
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E5E7EB",
          px: 3,
          py: 2,
        }}
      >
        {/* Top row: title (left) + primary actions (right) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: title ? "space-between" : "flex-end",
            gap: 1.5,
            mb: 1.5,
            flexWrap: "wrap",
          }}
        >
          {title && (
            <Typography
              component="h1"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "#1A2048",
                fontFamily: '"Inter Tight", sans-serif',
                flexShrink: 0,
              }}
            >
              {title}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {selection && selection.selectedIds.size > 0 && (
              <Tooltip
                title={t("calendar.clearSelectionTooltip", {
                  defaultValue: "Clear all selected meetings",
                })}
                arrow
                placement="bottom"
              >
                <Button
                  variant="text"
                  onClick={() => selection.onToggleAllVisible(false)}
                  sx={{
                    color: "#242C63",
                    fontFamily: '"Inter Tight", sans-serif',
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "6px",
                    "&:hover": {
                      backgroundColor: "rgba(36, 44, 99, 0.04)",
                    },
                  }}
                >
                  {t("calendar.clearSelection", {
                    count: selection.selectedIds.size,
                    defaultValue: `Clear selection (${selection.selectedIds.size})`,
                  })}
                </Button>
              </Tooltip>
            )}

            {selection && (
              <Tooltip
                title={
                  selection.selectedIds.size === 0
                    ? t("calendar.clientConnectionHint", {
                        defaultValue: "Select one or more meetings first",
                      })
                    : t("calendar.linkSelectedTooltip", {
                        defaultValue: "Connect selected meetings to a client",
                      })
                }
                arrow
                placement="bottom"
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={
                      selection.isLinking ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <LinkIcon />
                      )
                    }
                    disabled={
                      selection.selectedIds.size === 0 || selection.isLinking
                    }
                    onClick={selection.onOpenLinkDialog}
                    sx={{
                      borderColor: "#E5E7EB",
                      color: "#1A2048",
                      fontFamily: '"Inter Tight", sans-serif',
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: "6px",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#242C63",
                        backgroundColor: "rgba(36, 44, 99, 0.04)",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#E5E7EB",
                        color: "#C3C7CC",
                      },
                    }}
                  >
                    {t("calendar.clientConnection", {
                      defaultValue: "Client connection",
                    })}
                    {selection.selectedIds.size > 0
                      ? ` (${selection.selectedIds.size})`
                      : ""}
                  </Button>
                </span>
              </Tooltip>
            )}

            {onCreateMeeting && (
              <Button
                variant="contained"
                onClick={onCreateMeeting}
                sx={{
                  backgroundColor: "#242C63",
                  fontFamily: '"Inter Tight", sans-serif',
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#353D7A",
                    boxShadow: "none",
                  },
                }}
              >
                {t("calendar.createMeeting", {
                  defaultValue: "Create meeting",
                })}
              </Button>
            )}

            {connectionsSlot}

            <Button
              variant="outlined"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={onRefresh}
              disabled={loading}
              sx={{
                borderColor: "#242C63",
                color: "#242C63",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "6px",
                px: 2.5,
                py: 0.875,
                fontSize: "0.8125rem",
                fontFamily: '"Inter Tight", sans-serif',
                "&:hover": {
                  borderColor: "#353D7A",
                  backgroundColor: "rgba(36, 44, 99, 0.04)",
                },
              }}
            >
              {t("calendar.refresh")}
            </Button>
          </Box>
        </Box>

        {/* Line 2: Month nav (left) | Search + toggle (right) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                onClick={onPrevMonth}
                size="small"
                aria-label={t("calendar.previousMonth", {
                  defaultValue: "Previous month",
                })}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                  color: "#1A2048",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    borderColor: "#242C63",
                    backgroundColor: "rgba(36, 44, 99, 0.04)",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>

              <Button
                variant="outlined"
                onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  borderColor: "#E5E7EB",
                  color: "#1A2048",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "6px",
                  minWidth: 160,
                  boxShadow: "none",
                  fontFamily: '"Inter Tight", sans-serif',
                  "&:hover": {
                    borderColor: "#242C63",
                    backgroundColor: "rgba(36, 44, 99, 0.04)",
                    boxShadow: "none",
                  },
                }}
              >
                {dayjs(viewedMonth).format("MMMM YYYY")}
              </Button>

              <IconButton
                onClick={onNextMonth}
                size="small"
                aria-label={t("calendar.nextMonth", {
                  defaultValue: "Next month",
                })}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                  color: "#1A2048",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    borderColor: "#242C63",
                    backgroundColor: "rgba(36, 44, 99, 0.04)",
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>

              <Menu
                anchorEl={monthMenuAnchor}
                open={Boolean(monthMenuAnchor)}
                onClose={() => setMonthMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                      boxShadow:
                        "0px 16px 40px -8px rgba(36, 44, 99, 0.12)",
                      maxHeight: 360,
                      backgroundColor: "#ffffff",
                    },
                  },
                }}
              >
                {availableMonths.map((m) => {
                  const key = dayjs(m).format("YYYY-MM");
                  const isCurrentReal = dayjs(m).isSame(currentRealMonth, "month");
                  const isViewed = dayjs(m).isSame(viewedMonth, "month");
                  return (
                    <MenuItem
                      key={key}
                      onClick={() => {
                        onSetMonth(m);
                        setMonthMenuAnchor(null);
                      }}
                      sx={{
                        fontSize: "0.875rem",
                        minWidth: 200,
                        ...(isCurrentReal && {
                          backgroundColor: "#C3C7CC",
                          color: "#1A2048",
                          fontWeight: 600,
                          "&:hover": { backgroundColor: "#B8BCC2" },
                        }),
                      }}
                    >
                      <ListItemText
                        primary={dayjs(m).format("MMMM YYYY")}
                        slotProps={{
                          primary: {
                            sx: {
                              fontSize: "0.875rem",
                              fontWeight: isCurrentReal ? 600 : 400,
                              fontFamily: '"Inter Tight", sans-serif',
                            },
                          },
                        }}
                      />
                      {isViewed && (
                        <ListItemIcon sx={{ minWidth: "auto", ml: 2 }}>
                          <CheckIcon sx={{ fontSize: 16, color: "#242C63" }} />
                        </ListItemIcon>
                      )}
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>

            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={handleScrollToToday}
              sx={{
                borderColor: "#E5E7EB",
                color: "#1A2048",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "6px",
                boxShadow: "none",
                fontFamily: '"Inter Tight", sans-serif',
                "&:hover": {
                  borderColor: "#242C63",
                  backgroundColor: "rgba(36, 44, 99, 0.04)",
                  boxShadow: "none",
                },
              }}
            >
              {t("calendar.today")}
            </Button>
          </Box>

          {/* Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {onTogglePastInSearch && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!includePastInSearch}
                    onChange={(e) =>
                      onTogglePastInSearch(e.target.checked)
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#242C63",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#242C63",
                      },
                    }}
                  />
                }
                label={t("calendar.includePastInSearch", {
                  defaultValue: "Include meetings from the past in search",
                })}
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.8125rem",
                    color: "#64748B",
                    fontFamily: '"Inter Tight", sans-serif',
                    fontWeight: 500,
                  },
                }}
              />
            )}
            <Fade in={!!searchInProgress && isSearchActive} timeout={180}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid hsl(210, 11%, 86%)",
                  backgroundColor: "rgba(36, 44, 99, 0.05)",
                }}
              >
                <CircularProgress
                  size={14}
                  thickness={5}
                  sx={{ color: "#242C63" }}
                />
              </Box>
            </Fade>
            <TextField
              size="small"
              placeholder={t("search.calender_placeholder", {
                defaultValue: "Search calendar events…",
              })}
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              sx={{
                width: 280,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "white",
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: "#242C63" },
                },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#828CA1" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => onSearchChange("")}
                        sx={{ p: 0.25, color: "#828CA1" }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1280, mx: "auto", px: 3, py: 4 }}>
        {/* Select-all toolbar (selection variant only) */}
        {selection && totalVisible > 0 && (
          <Box
            sx={{
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
              }}
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => selection.onToggleAllVisible(e.target.checked)}
                sx={{
                  p: 0.5,
                  color: "#828CA1",
                  "&.Mui-checked": { color: "#242C63" },
                  "&.MuiCheckbox-indeterminate": { color: "#242C63" },
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {selectedVisibleCount > 0
                ? t("calendar.selectedCount", {
                    selected: selectedVisibleCount,
                    total: totalVisible,
                    defaultValue: `${selectedVisibleCount} of ${totalVisible} selected`,
                  })
                : t("calendar.selectAllVisible", {
                    count: totalVisible,
                    defaultValue: `Select all (${totalVisible})`,
                  })}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={30} />
          </Box>
        ) : grouped.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              fontSize: "0.875rem",
              color: "#828CA1",
              fontFamily: '"Inter Tight", sans-serif',
            }}
          >
            {isSearchActive
              ? t("calendar.noSearchResults", {
                  defaultValue: "No meetings match your search.",
                })
              : t("calendar.noEventsThisMonth", {
                  month: dayjs(viewedMonth).format("MMMM YYYY"),
                  defaultValue: `No meetings scheduled for ${dayjs(
                    viewedMonth,
                  ).format("MMMM YYYY")}.`,
                })}
          </Box>
        ) : (
          grouped.map(([date, dayEvents]) => {
            const parsed = dayjs(date);
            const isTodayGroup = parsed.isSame(dayjs(), "day");
            const isPastDay = parsed.isBefore(dayjs().startOf("day")) && !isTodayGroup;
            return (
              <DayGroup
                key={date}
                date={date}
                events={dayEvents}
                isTodayGroup={isTodayGroup}
                isPastDay={isPastDay}
                activeEventId={activeEventId}
                selection={selection}
                onEventClick={onEventClick}
                onChevronClick={onChevronClick}
                onRowDoubleClick={onRowDoubleClick}
                enableConflictDetection={enableConflictDetection}
                dayRef={date === todayKey ? todayRef : undefined}
              />
            );
          })
        )}
      </Box>
      <InfoSnackbar
        open={noTodayEventsSnackbar}
        onClose={() => setNoTodayEventsSnackbar(false)}
        message={t("calendar.noMeetingsToday", {
          defaultValue: "No meetings scheduled for today.",
        })}
      />
    </Box>
  );
};

export default MonthlyCalendarView;
